import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography, Card, Row, Col, Tag, Button, Input, Select, DatePicker,
  Upload, Space, Timeline, message, Spin, Empty,
  Descriptions, Modal, Dropdown,
} from 'antd';
import {
  ArrowLeftOutlined, ArrowRightOutlined, DeleteOutlined,
  RollbackOutlined, SaveOutlined, InboxOutlined, EyeOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { snsPostsApi, snsPostFilesApi } from '../api/endpoints';
import PostPreview from '../components/PostPreview';
import SortablePhotoGrid from '../components/SortablePhotoGrid';
import { STATUS_LABELS, STATUS_COLORS, PLATFORM_LABELS, PLATFORM_COLORS } from '../constants/status';
import { CONTENT_BOX, SPACING } from '../styles/tokens';
import type { SnsPost, SnsPostStatus, ActivityLog } from '../api/types';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

const STATUS_ORDER: SnsPostStatus[] = ['draft', 'writing', 'photo_requested', 'photo_received', 'ready', 'posted'];

const PostDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editData, setEditData] = useState<Partial<SnsPost>>({});

  const { data: post, isLoading } = useQuery({
    queryKey: ['sns-post', id],
    queryFn: () => snsPostsApi.get(Number(id)).then(r => (r.data as any).data ?? r.data),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<SnsPost>) => snsPostsApi.update(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sns-post', id] });
      setEditing(false);
      message.success('更新しました');
    },
    onError: () => message.error('更新に失敗しました'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ status, reason }: { status: string; reason?: string }) =>
      snsPostsApi.changeStatus(Number(id), status, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sns-post', id] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      message.success('ステータスを更新しました');
    },
    onError: (err: any) => message.error(err.response?.data?.error || 'ステータスの変更に失敗しました'),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => snsPostFilesApi.upload(Number(id), file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sns-post', id] });
      message.success('アップロードしました');
    },
    onError: (err: any) => message.error(err.response?.data?.error || 'アップロードに失敗しました'),
  });

  const deleteMutation = useMutation({
    mutationFn: (fileId: number) => snsPostFilesApi.delete(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sns-post', id] });
      message.success('削除しました');
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (fileIds: number[]) => snsPostFilesApi.reorder(Number(id), fileIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sns-post', id] });
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: () => snsPostsApi.delete(Number(id)),
    onSuccess: () => {
      message.success('投稿を削除しました');
      navigate('/posts');
    },
  });

  if (isLoading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!post) return <Empty description="投稿が見つかりません" />;

  const currentIndex = STATUS_ORDER.indexOf(post.status as SnsPostStatus);
  const nextStatus = currentIndex < STATUS_ORDER.length - 1 ? STATUS_ORDER[currentIndex + 1] : null;
  const prevStatus = currentIndex > 0 && post.status !== 'posted' ? STATUS_ORDER[currentIndex - 1] : null;

  const handleSave = () => {
    updateMutation.mutate(editData);
  };

  const startEdit = () => {
    setEditData({
      caption: post.caption,
      notes: post.notes,
      photo_instructions: post.photo_instructions,
      draft_caption: post.draft_caption,
      platform: post.platform,
      scheduled_date: post.scheduled_date,
    });
    setEditing(true);
  };

  const handleUpload = (file: File) => {
    uploadMutation.mutate(file);
    return false;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: SPACING.lg }}>
        {/* Row 1: Title & Meta */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/posts')}>戻る</Button>
            <Title level={4} style={{ margin: 0 }}>
              {post.store?.name}
            </Title>
          </Space>
          <Space>
            <Tag color={STATUS_COLORS[post.status as SnsPostStatus]}>
              {STATUS_LABELS[post.status as SnsPostStatus]}
            </Tag>
            <Tag color={PLATFORM_COLORS[post.platform as keyof typeof PLATFORM_COLORS]}>
              {PLATFORM_LABELS[post.platform as keyof typeof PLATFORM_LABELS]}
            </Tag>
            <Text type="secondary">{post.scheduled_date}</Text>
          </Space>
        </div>

        {/* Row 2: Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <Space>
            {prevStatus && (
              <Button
                icon={<RollbackOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: `ステータスを「${STATUS_LABELS[prevStatus]}」に戻しますか？`,
                    onOk: () => statusMutation.mutate({ status: prevStatus }),
                  });
                }}
                loading={statusMutation.isPending}
              >
                ←{STATUS_LABELS[prevStatus]}に戻す
              </Button>
            )}
            {nextStatus && (
              <Button
                type="primary"
                icon={<ArrowRightOutlined />}
                onClick={() => statusMutation.mutate({ status: nextStatus })}
                loading={statusMutation.isPending}
              >
                {STATUS_LABELS[nextStatus]}へ進む→
              </Button>
            )}
          </Space>
          <Space>
            <Button icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>プレビュー</Button>
            <Dropdown
              menu={{
                items: [
                  { label: '削除', key: 'delete', danger: true, icon: <DeleteOutlined /> },
                ],
                onClick: ({ key }) => {
                  if (key === 'delete') {
                    Modal.confirm({
                      title: 'この投稿を削除しますか？',
                      okText: '削除',
                      cancelText: 'キャンセル',
                      okButtonProps: { danger: true },
                      onOk: () => deletePostMutation.mutate(),
                    });
                  }
                },
              }}
              trigger={['click']}
            >
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        </div>
      </div>

      <Row gutter={24}>
        {/* Left: Caption */}
        <Col xs={24} lg={14}>
          <Card
            title="原稿"
            extra={
              editing ? (
                <Space>
                  <Button onClick={() => setEditing(false)}>キャンセル</Button>
                  <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={updateMutation.isPending}>保存</Button>
                </Space>
              ) : (
                <Button onClick={startEdit}>編集</Button>
              )
            }
          >
            {editing ? (
              <>
                <div style={{ marginBottom: SPACING.md }}>
                  <Text strong>プラットフォーム</Text>
                  <Select
                    value={editData.platform}
                    onChange={(v) => setEditData({ ...editData, platform: v })}
                    style={{ width: '100%', marginTop: SPACING.sm }}
                    options={Object.entries(PLATFORM_LABELS).map(([k, v]) => ({ value: k, label: v }))}
                  />
                </div>
                <div style={{ marginBottom: SPACING.md }}>
                  <Text strong>予定日</Text>
                  <DatePicker
                    value={editData.scheduled_date ? dayjs(editData.scheduled_date) : null}
                    onChange={(d) => setEditData({ ...editData, scheduled_date: d?.format('YYYY-MM-DD') })}
                    style={{ width: '100%', marginTop: SPACING.sm }}
                  />
                </div>
                <div style={{ marginBottom: SPACING.md }}>
                  <Text strong>初期草稿</Text>
                  <TextArea
                    rows={4}
                    value={editData.draft_caption || ''}
                    onChange={(e) => setEditData({ ...editData, draft_caption: e.target.value })}
                    style={{ marginTop: SPACING.sm }}
                    placeholder="投稿の初期草稿・アイデアを入力..."
                  />
                </div>
                <div style={{ marginBottom: SPACING.md }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong>確定原稿</Text>
                    <Text type="secondary">{(editData.caption || '').length}文字</Text>
                  </div>
                  <TextArea
                    rows={8}
                    value={editData.caption || ''}
                    onChange={(e) => setEditData({ ...editData, caption: e.target.value })}
                    style={{ marginTop: SPACING.sm }}
                    placeholder="投稿の確定原稿を入力..."
                  />
                </div>
                <div>
                  <Text strong>メモ</Text>
                  <TextArea
                    rows={3}
                    value={editData.notes || ''}
                    onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                    style={{ marginTop: SPACING.sm }}
                    placeholder="社内メモ（任意）"
                  />
                </div>
              </>
            ) : (
              <>
                <Descriptions column={2} style={{ marginBottom: SPACING.md }}>
                  <Descriptions.Item label="プラットフォーム">
                    <Tag color={PLATFORM_COLORS[post.platform as keyof typeof PLATFORM_COLORS]}>
                      {PLATFORM_LABELS[post.platform as keyof typeof PLATFORM_LABELS]}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="予定日">{post.scheduled_date}</Descriptions.Item>
                </Descriptions>
                <div style={{ marginBottom: SPACING.md }}>
                  <Text strong>確定原稿</Text>
                  <div style={{ ...CONTENT_BOX.confirmed, marginTop: SPACING.sm }}>
                    {post.caption || <Text type="secondary">確定原稿が未入力です</Text>}
                  </div>
                </div>
                <div style={{ marginBottom: SPACING.md }}>
                  <Text type="secondary" strong>初期草稿</Text>
                  <div style={{ ...CONTENT_BOX.draft, marginTop: SPACING.sm }}>
                    {post.draft_caption || <Text type="secondary">初期草稿が未入力です</Text>}
                  </div>
                </div>
                <div>
                  <div style={{ ...CONTENT_BOX.notes, marginTop: SPACING.sm }}>
                    {post.notes || <Text type="secondary">メモが未入力です</Text>}
                  </div>
                </div>
              </>
            )}
          </Card>
        </Col>

        {/* Right: Photos */}
        <Col xs={24} lg={10}>
          <Card title={`写真（${post.sns_post_files?.length || 0}枚）`}>
            <Dragger
              beforeUpload={(file) => handleUpload(file)}
              showUploadList={false}
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              style={{ marginBottom: SPACING.md }}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">クリックまたはドラッグで写真をアップロード</p>
              <p className="ant-upload-hint">JPEG, PNG, GIF, WebP対応</p>
            </Dragger>

            {(post.sns_post_files?.length || 0) === 0 ? (
              <Empty description="写真がありません" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <SortablePhotoGrid
                files={[...(post.sns_post_files || [])].sort((a, b) => a.sort_order - b.sort_order)}
                onDelete={(fileId) => deleteMutation.mutate(fileId)}
                onReorder={(fileIds) => reorderMutation.mutate(fileIds)}
              />
            )}
          </Card>

          <Card title="撮影依頼内容" style={{ marginTop: SPACING.md }}>
            {editing ? (
              <TextArea
                rows={4}
                value={editData.photo_instructions || ''}
                onChange={(e) => setEditData({ ...editData, photo_instructions: e.target.value })}
                placeholder="撮影してほしい内容・アングル・注意点など..."
              />
            ) : (
              <div style={CONTENT_BOX.photoRequest}>
                {post.photo_instructions || <Text type="secondary">撮影依頼内容が未入力です</Text>}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Activity Log */}
      <Card title="作業ログ" style={{ marginTop: SPACING.lg }}>
        {(post.activity_logs?.length || 0) === 0 ? (
          <Empty description="作業ログがありません" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Timeline
            items={post.activity_logs?.map((log: ActivityLog) => ({
              color: log.action === 'status_changed' ? 'blue' : log.action === 'file_uploaded' ? 'green' : 'gray',
              children: (
                <div>
                  <Text strong>{log.user?.username}</Text>
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {dayjs(log.created).format('YYYY/MM/DD HH:mm')}
                  </Text>
                  <div>{log.description}</div>
                </div>
              ),
            }))}
          />
        )}
      </Card>

      <Modal
        title="投稿プレビュー"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={560}
      >
        {post && <PostPreview post={post} />}
      </Modal>
    </div>
  );
};

export default PostDetailPage;
